import { Box, Collapse, IconButton, Tooltip } from "@mui/material";
import { AxiosResponse } from "axios";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import BootstrapModal from "react-bootstrap/Modal";
import Select from "react-select";
import { toast } from "react-toastify";
import RequisitionType from "Services/Requisition/RequisitionTypeService";
import { Loader } from "Shared/Common/Loader";
import useLang from "Shared/hooks/useLanguage";
import { styles } from "Utils/Common";

interface InternalNotesCollapseProps {
  open: boolean;
  onClose: () => void;
  rowData: any;
  hideHeader?: boolean;
  hideOrderAccession?: boolean;
}

// Helper to get case-insensitive key from row data
const getNormalizedValue = (obj: any, key: string) => {
  if (!obj || !key) return undefined;
  const normalizedKey = Object.keys(obj).find(
    (k: string) => k.toLowerCase() === key.toLowerCase()
  );
  return normalizedKey ? obj[normalizedKey] : undefined;
};

// Helper to format backend date ("2026-02-17 10:40 AM") as "02/17/2026 10:40 AM"
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "";

  try {
    // Expecting: "YYYY-MM-DD hh:mm AM/PM"
    const match = dateString.match(
      /^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i
    );

    if (!match) {
      // If it doesn't match expected pattern, just return as-is
      return dateString;
    }

    const [, year, month, day, hour, minute, ampm] = match;

    // Format as MM/DD/YYYY H:MM AM/PM (no seconds, no time math)
    const monthOut = month.padStart(2, "0");
    const dayOut = day.padStart(2, "0");
    const hourOut = String(parseInt(hour, 10)); // remove any leading zero

    return `${monthOut}/${dayOut}/${year} ${hourOut}:${minute} ${ampm.toUpperCase()}`;
  } catch (error) {
    return dateString; // Return original if formatting fails
  }
};

const InternalNotesCollapse: React.FC<InternalNotesCollapseProps> = ({
  open,
  onClose,
  rowData,
  hideHeader = false,
  hideOrderAccession = false,
}) => {
  const { t } = useLang();
  const queryClient = useQueryClient();

  const [selectedNoteLabel, setSelectedNoteLabel] = useState<any>(null);
  const [noteText, setNoteText] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [deletingNoteId, setDeletingNoteId] = useState<number | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<number | null>(null);

  // Extract IDs from row data (case-insensitive)
  const requisitionOrderId = getNormalizedValue(rowData, "requisitionorderid");
  const requisitionId = getNormalizedValue(rowData, "requisitionid");
  const requisitionTypeId = getNormalizedValue(rowData, "requisitiontypeid");
  // Get Order/Accession values from rowData
  const orderNumber =
    getNormalizedValue(rowData, "NoteOrderNo") ||
    getNormalizedValue(rowData, "Order") ||
    "";
  const accessionNumber =
    getNormalizedValue(rowData, "NoteAccessionNumber") ||
    getNormalizedValue(rowData, "AccessionNumber") ||
    "";

  // ─── React Query: Fetch note labels lookup ───
  const { data: noteLabelOptions = [] } = useQuery(
    "requisition-note-label-lookup",
    () => RequisitionType.GetRequisitionNoteLabel_Lookup(),
    {
      enabled: open,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      select: (res: AxiosResponse) => {
        const data = res?.data?.data || res?.data || [];
        return Array.isArray(data) ? data : [];
      },
      staleTime: 5 * 60 * 1000, // cache for 5 minutes
    }
  );

  // ─── React Query: Fetch internal notes ───
  const notesQueryKey = [
    "internal-notes",
    requisitionOrderId,
    requisitionId,
  ];

  const {
    data: notesResponse,
    isLoading: isLoadingNotes,
    refetch: refetchNotes,
  } = useQuery(
    notesQueryKey,
    () => {
      const payload = {
        pageNumber: 1,
        pageSize: 50,
        queryModel: {
          requisitionOrderId: requisitionOrderId
            ? parseInt(requisitionOrderId)
            : 0,
          requisitionId: requisitionId ? parseInt(requisitionId) : 0,
          note: "",
          createdBy: "",
          createdDate: "",
          updatedBy: "",
          updatedDate: "",
          noteLabel: "",
          isPaginated: false
        },
        sortColumn: "",
        sortDirection: "",
      };
      return RequisitionType.GetInternalNotes(payload);
    },
    {
      enabled: open && !!requisitionOrderId && !!requisitionId,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      select: (res: AxiosResponse) => {
        const responseData = res?.data?.data;
        const notesData = Array.isArray(responseData) ? responseData : [];
        return {
          notes: notesData,
        };
      },
    }
  );

  const notes = notesResponse?.notes || [];

  const saveNoteMutation = useMutation(
    (payload: any) => RequisitionType.SaveRequisitionInternalNote(payload),
    {
      onSuccess: (res: AxiosResponse) => {
        if (res?.data?.status === 200) {
          toast.success(
            t(
              res?.data?.message ||
              res?.data?.responseMessage ||
              "Saved successfully"
            )
          );
          setNoteText("");
          setSelectedNoteLabel(null);
          setEditingNoteId(null);
          // Invalidate all internal-notes queries (any requisition),
          // then explicitly refetch this instance to ensure UI is fresh.
          queryClient.invalidateQueries("internal-notes");
          refetchNotes();
        } else {
          toast.error(
            t(
              res?.data?.message ||
              res?.data?.responseMessage ||
              "Something went wrong while saving the note."
            )
          );
        }
      },
      onError: () => {
        toast.error(t("Something went wrong while saving the note."));
      },
    }
  );

  const deleteNoteMutation = useMutation(
    (noteId: number) => RequisitionType.DeleteRequisitionInternalNote(noteId),
    {
      onSuccess: (res: AxiosResponse) => {
        if (res?.data?.httpStatusCode === 200) {
          toast.success(t(res?.data?.message || res?.data?.responseMessage || "Deleted successfully"));
          setDeletingNoteId(null);
          queryClient.invalidateQueries("internal-notes");
          refetchNotes();
        }
      },
      onError: () => {
        setDeletingNoteId(null);
        toast.error(t("Something went wrong while deleting the note."));
      },
    }
  );

  useEffect(() => {
    if (!open) {
      setEditingNoteId(null);
      setNoteText("");
      setSelectedNoteLabel(null);
      setShowDeleteDialog(false);
      setNoteToDelete(null);
    }
  }, [open, requisitionOrderId]);

  const handleSaveOrUpdate = () => {
    if (!noteText.trim() || !selectedNoteLabel?.value) {
      toast.error(t("Please fill in all mandatory fields to proceed."));
      return;
    }

    const payload: any = {
      requisitionId: requisitionId ? parseInt(requisitionId) : 0,
      requisitionOrderId: requisitionOrderId
        ? parseInt(requisitionOrderId)
        : 0,
      requsitionType: requisitionTypeId ? parseInt(requisitionTypeId) : 2,
      note: noteText.trim(),
      noteLabel: selectedNoteLabel?.value || "",
    };

    // If editing, include the id so backend updates instead of creating
    if (editingNoteId !== null) {
      payload.id = editingNoteId;
    }

    saveNoteMutation.mutate(payload);
  };

  const handleEditStart = (note: any) => {
    setNoteText(note.Note || "");
    const matchingLabel = noteLabelOptions.find(
      (opt: any) => opt.value === note.NoteLabel
    );
    setSelectedNoteLabel(matchingLabel || null);
    setEditingNoteId(note.Id);
  };

  const handleEditCancel = () => {
    setEditingNoteId(null);
    setNoteText("");
    setSelectedNoteLabel(null);
  };

  const handleDelete = (noteId: number) => {
    // Show confirmation dialog
    setNoteToDelete(noteId);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (noteToDelete === null) return;

    if (editingNoteId === noteToDelete) {
      setEditingNoteId(null);
      setNoteText("");
      setSelectedNoteLabel(null);
    }
    setDeletingNoteId(noteToDelete);
    deleteNoteMutation.mutate(noteToDelete);
    setShowDeleteDialog(false);
    setNoteToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setNoteToDelete(null);
  };

  const handleClose = () => {
    setEditingNoteId(null);
    setNoteText("");
    setSelectedNoteLabel(null);
    onClose();
  };

  const isSaving = saveNoteMutation.isLoading;
  const isDeleting = deleteNoteMutation.isLoading;

  return (
    <Collapse in={open} unmountOnExit>
      <div
        className="card mb-3"
        style={{
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: "#FFFFFF",
          border: hideHeader ? "none" : "1px solid #e8e5dc",
        }}
      >
        {/* Header */}
        {!hideHeader && (
          <div
            className="d-flex align-items-center justify-content-between px-4 py-3"
            style={{
              backgroundColor: "#FFF4DE",
              borderBottom: "1px solid #e8dfc0",
            }}
          >
            <h5 className="m-0 fw-bold" style={{ color: "#333" }}>
              {t("Internal Notes")}
            </h5>
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-secondary btn-sm fw-500"
                style={{
                  color: '#181C32',
                  borderRadius: "8px",
                  minWidth: "80px",
                }}
                onClick={handleClose}
                disabled={isSaving}
              >
                {t("Cancel")}
              </button>
              <button
                className="btn btn-primary btn-sm fw-500"
                style={{
                  borderRadius: "8px",
                  minWidth: "70px",
                }}
                onClick={handleSaveOrUpdate}
                disabled={isSaving}
              >
                {isSaving
                  ? t("Saving...")
                  : editingNoteId !== null
                    ? t("Update")
                    : t("Save")}
              </button>
            </div>
          </div>
        )}

        {/* Show Save/Cancel buttons at bottom when header is hidden */}
        {hideHeader && (
          <div className="d-flex align-items-center justify-content-end gap-2 px-4 py-3">
            <button
              className="btn btn-secondary btn-sm fw-500"
              style={{
                color: '#181C32',
                borderRadius: "8px",
                minWidth: "80px",
              }}
              onClick={handleClose}
              disabled={isSaving}
            >
              {t("Cancel")}
            </button>
            <button
              className="btn btn-sm fw-bold px-4"
              style={{
                backgroundColor: "#4caf50",
                color: "#fff",
                borderRadius: "8px",
                minWidth: "80px",
                border: "none",
              }}
              onClick={handleSaveOrUpdate}
              disabled={isSaving}
            >
              {isSaving
                ? t("Saving...")
                : editingNoteId !== null
                  ? t("Update")
                  : t("Save")}
            </button>
          </div>
        )}

        {/* Body */}
        <div className="px-4 py-3" style={{ backgroundColor: "#FFFFFF" }}>
          {/* Order & Accession Info Badges */}
          {!hideOrderAccession && <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <span
              className="fw-500 px-3 py-1"
              style={{
                backgroundColor: "#E9EEF4",
                borderRadius: "20px",
                fontSize: "13px",
                color: "#333",
              }}
            >
              {t("Order #")}:&nbsp;&nbsp;
              <strong>{orderNumber}</strong>
            </span>
            <span
              className="fw-500 px-3 py-1"
              style={{
                backgroundColor: "#E9EEF4",
                borderRadius: "20px",
                fontSize: "13px",
                color: "#333",
              }}
            >
              {t("Accession no")}:&nbsp;&nbsp;
              <strong>{accessionNumber}</strong>
            </span>
          </Box>}

          {/* Note Label & Note Input Row */}
          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <div style={{ flex: "0 0 250px" }}>
              <label
                className="form-label mb-1 fw-500"
                style={{ fontSize: "13px", color: "#333" }}
              >
                {t("Note Label")}
              </label>
              <Select
                options={noteLabelOptions}
                placeholder={t("--- Select ---")}
                theme={(theme) => styles(theme)}
                isSearchable
                isClearable
                value={selectedNoteLabel}
                onChange={(val: any) => setSelectedNoteLabel(val)}
                formatOptionLabel={(option: any) => (
                  <div className="d-flex align-items-center gap-2">
                    <span
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: option.value,
                        display: "inline-block",
                        flexShrink: 0,
                      }}
                    />
                    <span>{option.label}</span>
                  </div>
                )}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderColor: "var(--kt-input-border-color)",
                    backgroundColor: "#fff",
                    minHeight: "38px",
                    borderRadius: "8px",
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
                menuPortalTarget={document.body}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label
                className="form-label mb-1 fw-500"
                style={{ fontSize: "13px", color: "#333" }}
              >
                {t("Note")}
              </label>
              <input
                type="text"
                className="form-control bg-white"
                placeholder={t("Note...")}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSaveOrUpdate();
                  }
                  if (e.key === "Escape" && editingNoteId !== null) {
                    handleEditCancel();
                  }
                }}
                style={{ borderRadius: "8px", height: "38px" }}
              />
              {editingNoteId !== null && (
                <small
                  className="cursor-pointer mt-1 d-inline-block"
                  style={{ color: "#d48806", textDecoration: "underline" }}
                  onClick={handleEditCancel}
                >
                  {t("Cancel editing")}
                </small>
              )}
            </div>
          </Box>

          {/* Notes List */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              maxHeight: "200px",
              overflowY: "auto",
              pr: 0.5,
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#c4c4c4",
                borderRadius: 3,
              },
            }}
          >
            {isLoadingNotes ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 3,
                }}
              >
                <Loader />
              </Box>
            ) : notes.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  color: "#999",
                  fontStyle: "italic",
                }}
              >
                {t("No notes to display.")}
              </Box>
            ) : (
              notes.map((note: any) => (
                <Box
                  key={note.Id}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    borderLeft: `4px solid ${note.NoteLabel || "#FFFFFF"}`,
                    bgcolor: "#F5F8FA",
                    borderRadius: "4px",
                    px: 1.5,
                    py: 0.8,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                    "&:hover": {
                      boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                    },
                    ...(editingNoteId === note.Id && {
                      outline: "2px solid #ffc107",
                    }),
                  }}
                >
                  {/* Note Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      {note.UpdatedDate && (
                        <span
                          style={{
                            fontSize: "11px",
                            color: "#ff9800",
                            fontWeight: 600,
                          }}
                        >
                          ({t("Edited")})
                        </span>
                      )}
                      <span
                        style={{
                          fontSize: "13px",
                          color: "#444",
                          lineHeight: 1.5,
                        }}
                      >
                        {note.Note}
                      </span>
                    </Box>
                  </Box>

                  {/* Author, Date & Actions */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexShrink: 0,
                      ml: 2,
                      gap: 1.5,
                    }}
                  >

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {note.CanEdit && (
                        <Tooltip title={t("Edit")} arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleEditStart(note)}
                            disabled={
                              isSaving || deletingNoteId === note.Id
                            }
                            sx={{
                              bgcolor: "#e8f5e9",
                              borderRadius: "4px",
                              width: 26,
                              height: 26,
                              "&:hover": { bgcolor: "#c8e6c9" },
                              "&.Mui-disabled": {
                                opacity: 0.5,
                              },
                            }}
                          >
                            <i
                              className="fa fa-edit"
                              style={{ fontSize: "11px", color: "#4caf50" }}
                            ></i>
                          </IconButton>
                        </Tooltip>
                      )}
                      {note.CanDelete && (
                        <Tooltip title={t("Delete")} arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(note.Id)}
                            disabled={
                              isSaving || deletingNoteId === note.Id
                            }
                            sx={{
                              bgcolor: "#ffebee",
                              borderRadius: "4px",
                              width: 26,
                              height: 26,
                              "&:hover": { bgcolor: "#ffcdd2" },
                              "&.Mui-disabled": {
                                opacity: 0.5,
                              },
                            }}
                          >
                            <i
                              className="fa fa-trash"
                              style={{ fontSize: "11px", color: "#f44336" }}
                            ></i>
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>


                    <Box sx={{ textAlign: "right", minWidth: "140px" }}>
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#555",
                        }}
                      >
                        {note.UpdatedDate ? note.UpdatedBy : note.CreatedBy}
                      </div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#888",
                        }}
                      >
                        {note.UpdatedDate
                          ? formatDate(note.UpdatedDate)
                          : formatDate(note.CreatedDate)}
                      </div>
                    </Box>


                  </Box>
                </Box>
              ))
            )}
          </Box>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <BootstrapModal
        show={showDeleteDialog}
        onHide={handleCancelDelete}
        backdrop="static"
        keyboard={false}
      >
        <BootstrapModal.Header closeButton className="bg-light-primary m-0 p-5">
          <h4>{t("Warning")}</h4>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          {t("Are you sure you want to delete this note?")}
        </BootstrapModal.Body>
        <BootstrapModal.Footer className="p-0">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancelDelete}
            disabled={isDeleting}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="btn btn-danger m-2"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? t("Deleting...") : t("Delete")}
          </button>
        </BootstrapModal.Footer>
      </BootstrapModal>
    </Collapse>
  );
};

export default InternalNotesCollapse;
